import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllUsers } from "../store/slices/authSlice";
import { updateEmployeeSalaryDetails } from "../store/slices/salarySlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import {
  ChevronDown,
  DollarSign,
  CreditCard,
  Save,
  WalletCards,
  BadgeMinus,
  Ribbon,
  Building,
} from "lucide-react";

const SalaryManagement = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.auth);
  const { activeSociety, theme, gradient } = useSidebarContext();

  const [salaryDetails, setSalaryDetails] = useState({
    salary: 0,
    allowances: 0,
    bonuses: 0,
    deductions: 0,
  });

  const [selectedUsername, setSelectedUsername] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [previousSocietyId, setPreviousSocietyId] = useState(null);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (activeSociety?._id !== previousSocietyId) {
      setSelectedUsername("");
      setEmployeeId(null);
      setSalaryDetails({
        salary: 0,
        allowances: 0,
        bonuses: 0,
        deductions: 0,
      });
      setPreviousSocietyId(activeSociety?._id);
    }
  }, [activeSociety, previousSocietyId]);

  const societyEmployees = useMemo(() => {
    if (!activeSociety || !users) return [];
    return users.filter((user) =>
      activeSociety.users.some(
        (societyUser) =>
          societyUser._id === user._id &&
          user.employeeData &&
          user.role === "Employee"
      )
    );
  }, [activeSociety, users]);

  useEffect(() => {
    if (selectedUsername && societyEmployees.length > 0) {
      const selectedUser = societyEmployees.find(
        (user) => user.username === selectedUsername
      );

      if (selectedUser && selectedUser.employeeData) {
        const { salaryDetails: userSalaryDetails, _id: userEmployeeId } =
          selectedUser.employeeData;

        if (userSalaryDetails) {
          const { salary, allowances, bonuses, deductions } = userSalaryDetails;

          setSalaryDetails({
            salary: salary || 0,
            allowances: allowances || 0,
            bonuses: bonuses || 0,
            deductions: deductions || 0,
          });

          setEmployeeId(userEmployeeId);
        } else {
          setSalaryDetails({
            salary: 0,
            allowances: 0,
            bonuses: 0,
            deductions: 0,
          });
          setEmployeeId(userEmployeeId);
        }
      }
    }
  }, [selectedUsername, societyEmployees]);

  const handleUpdateSalaryDetails = () => {
    if (employeeId) {
      const payload = {
        employeeId,
        salaryDetails: {
          ...salaryDetails,
          cnic:
            users.find((user) => user._id === employeeId)?.employeeData
              ?.personalDetails?.cnic || "default_cnic_value",
        },
      };
      dispatch(updateEmployeeSalaryDetails(payload));
    } else {
      console.error("No employeeId provided for updating salary details.");
    }
  };

  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const parseFormattedNumber = (value) => {
    return parseFloat(value.replace(/,/g, "")) || 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFormattedNumber(value);
    setSalaryDetails({ ...salaryDetails, [name]: numericValue });
  };

  const calculateNetSalary = () => {
    const { salary, allowances, bonuses, deductions } = salaryDetails;
    return formatNumber(salary + allowances + bonuses - deductions);
  };

  if (!activeSociety) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className={`${theme.tailwind.text9} text-xl`}>
          Please select a society to manage employee salaries.
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-6">
      <div
        className={`w-full max-w-4xl bg-slate-900/60 backdrop-blur-sm rounded-2xl shadow-2xl border ${theme.tailwind.border1} overflow-hidden`}
        style={{
          backgroundImage: `
        linear-gradient(to bottom right, ${theme.mui.bg2}),
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
      `,
        }}
      >
        <div className={`p-6 border-b ${theme.tailwind.border1}`}>
          <div
            className={`relative overflow-hidden rounded-lg p-6 ${gradient} shadow-xl`}
          >
            <div className="absolute inset-0 opacity-10">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="10"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                      className={`${theme.tailwind.text9}`}
                    />
                  </pattern>
                  <pattern
                    id="circles"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="2"
                      fill="currentColor"
                      className={`${theme.tailwind.text9}`}
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)" />
                <rect width="100" height="100" fill="url(#circles)" />
              </svg>
            </div>

            <div className="relative">
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-3 group">
                  <div
                    className={`p-2 rounded-lg ${theme.tailwind.bg11} transform group-hover:scale-110 transition-transform duration-300`}
                  >
                    <DollarSign className={`w-8 h-8 ${theme.tailwind.text9}`} />
                  </div>
                  <h2
                    className={`text-3xl font-bold ${theme.tailwind.text9} tracking-tight`}
                  >
                    Salary Management
                  </h2>
                </div>

                <div
                  className={`flex items-center space-x-2 ml-14 ${theme.tailwind.text4} ${theme.tailwind.hover1} transition-colors duration-300`}
                >
                  <Building className="w-5 h-5 m" />
                  <span className="text-lg">{activeSociety.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Employee Dropdown */}
          <div>
            <label
              className={`block ${theme.tailwind.text9} mb-2 flex items-center`}
            >
              <ChevronDown className="mr-2 w-5 h-5" />
              Select Employee
            </label>
            <div className="relative">
              <select
                value={selectedUsername}
                onChange={(e) => setSelectedUsername(e.target.value)}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 appearance-none focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} transition-all duration-300`}
              >
                <option value="">-- Select Username --</option>
                {societyEmployees.map((user) => (
                  <option
                    key={user._id}
                    value={user.username}
                    className={`${theme.tailwind.bg14} ${theme.tailwind.text9}`}
                  >
                    {user.username} - {user.employeeData.designation}
                  </option>
                ))}
              </select>
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${theme.tailwind.text9}`}
              >
                <ChevronDown className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Salary Details Inputs */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label
                className={`block ${theme.tailwind.text9} mb-2 flex items-center`}
              >
                <CreditCard className="mr-2 w-5 h-5" />
                Salary
              </label>
              <input
                type="text"
                name="salary"
                value={formatNumber(salaryDetails.salary)}
                onChange={handleChange}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} transition-all duration-300"`}
              />
            </div>
            <div>
              <label
                className={`block ${theme.tailwind.text9} mb-2 flex items-center`}
              >
                <WalletCards className="mr-2 w-5 h-5" />
                Allowances
              </label>
              <input
                type="text"
                name="allowances"
                value={formatNumber(salaryDetails.allowances)}
                onChange={handleChange}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} transition-all duration-300"`}
              />
            </div>
            <div>
              <label
                className={`block ${theme.tailwind.text9} mb-2 flex items-center`}
              >
                <Ribbon className="mr-2 w-5 h-5" />
                Bonuses
              </label>
              <input
                type="text"
                name="bonuses"
                value={formatNumber(salaryDetails.bonuses)}
                onChange={handleChange}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} transition-all duration-300"`}
              />
            </div>
            <div>
              <label
                className={`block ${theme.tailwind.text9} mb-2 flex items-center`}
              >
                <BadgeMinus className="mr-2 w-5 h-5" />
                Deductions
              </label>
              <input
                type="text"
                name="deductions"
                value={formatNumber(salaryDetails.deductions)}
                onChange={handleChange}
                className={`w-full ${theme.tailwind.bg14} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} transition-all duration-300"`}
              />
            </div>
          </div>

          {/* Net Salary Display */}
          <div
            className={`${theme.tailwind.bg14} rounded-lg p-4 border ${theme.tailwind.border1}`}
          >
            <div className="flex justify-between items-center">
              <span className={`${theme.tailwind.text9} font-semibold`}>
                Net Salary
              </span>
              <span className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                PKR {calculateNetSalary()}
              </span>
            </div>
          </div>

          {/* Update Button */}
          <button
            onClick={handleUpdateSalaryDetails}
            disabled={!selectedUsername}
            className={`w-full ${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white py-3 rounded-lg flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-[1.02] focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
          >
            <Save className="w-5 h-5 mr-2" />
            Update Salary Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalaryManagement;
